import { useCallback, useEffect, useState } from "react";
import type { ConfigResponse } from "pages/api/config";
import { asyncVoid } from "utils/asyncVoidHandler";

const CONFIG_LOADING_EVENT = "configLoading";
const CONFIG_FINISHED_EVENT = "configFinished";
const CONFIG_CHANGE_EVENT = "configChange";

class ConfigService extends EventTarget {
  public config: ConfigResponse | undefined;

  private activeFetch: Promise<ConfigResponse> | undefined;

  async get() {
    if (this.config) return this.config;

    if (!this.activeFetch) {
      this.activeFetch = this.fetch();
    }

    return this.activeFetch;
  }

  private async fetch() {
    try {
      this.dispatchEvent(new Event(CONFIG_LOADING_EVENT));
      const response = await window.fetch("/api/config");
      const data = (await response.json()) as ConfigResponse;
      this.config = data;
      this.dispatchEvent(new Event(CONFIG_CHANGE_EVENT));
      return data;
    } finally {
      this.dispatchEvent(new Event(CONFIG_FINISHED_EVENT));
    }
  }
}

const singleton = new ConfigService();

export default singleton;

function useConfigChangeListener(): [
  config: ConfigResponse | undefined,
  isLoading: boolean,
] {
  const [config, setConfig] = useState<ConfigResponse | undefined>(
    singleton.config,
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  const handleFinished = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleChange = useCallback(() => {
    setConfig(singleton.config);
  }, []);

  useEffect(() => {
    singleton.addEventListener(CONFIG_LOADING_EVENT, handleLoading);
    singleton.addEventListener(CONFIG_FINISHED_EVENT, handleFinished);
    singleton.addEventListener(CONFIG_CHANGE_EVENT, handleChange);
    return () => {
      singleton.removeEventListener(CONFIG_LOADING_EVENT, handleLoading);
      singleton.removeEventListener(CONFIG_FINISHED_EVENT, handleFinished);
      singleton.removeEventListener(CONFIG_CHANGE_EVENT, handleChange);
    };
  }, [handleLoading, handleFinished, handleChange]);

  return [config, isLoading];
}

export function useConfig() {
  const result = useConfigChangeListener();

  useEffect(() => {
    asyncVoid(singleton.get(), false);
  }, []);

  return result;
}
